import { useTranslation } from 'react-i18next'
import { FunctionComponent, SVGProps, useMemo, useCallback, useState } from 'react'
import {
  Box,
  Button,
  Text,
  Table,
  TableColumnDefinition,
  Stack,
  useDialogControllers,
  ResponsiveRender
} from '@machobear/design-system'
import { pipe, propEq, prop, map, evolve, filter, reject, isEmpty, flatten, anyPass } from 'ramda'
import { TenantScreening, RentalAdvertising, InfoCircleDarkBlue } from '@/components/icons'
import useMyListings from '@/pages/Listing/MyListings/hooks/use-my-listings'
import useGetPlanKey from '@/hooks/use-get-plan-key'
import MobileCard from './moblile-card'
import { productStyle } from './index.css'
import DialogWrapper from '../Dialogs/dialog-wrapper'
import { getListingLabel } from '../../utils/get-listing-label'

type Props = {
  hasPurchasedCreditChecks: boolean
  hasPurchasedSponsoredListing: boolean
  hasPurchasedSyndications: boolean
  purchasedCredits: { creditChecks: number; sponsoredListings: number; syndications: number }
}
type modalOptions =
  | 'sponsoredListingDialog'
  | 'tenantScreeningOptionsDialog'
  | 'tenantScreeningDialog'
  | 'warningDialog'
  | 'useSyndicationCredits'
  | 'syndicationDialog'

type TableItem = {
  title: string
  icon: FunctionComponent<SVGProps<SVGSVGElement>>
  credits: string
  useCreditId: string
  useCreditDisabled: boolean
  useCreditOnClick: () => void
  buyMoreOnClick?: () => void
  buyMoreId?: string
}
const filterActiveListings = pipe(
  map(
    evolve({
      listings: filter(
        anyPass([propEq('state_machine', 'published'), propEq('state_machine', 'pending')])
      )
    })
  ),
  reject((building: any) => isEmpty(building.listings)),
  map(prop('listings')),
  flatten
)

const ALaCarte = ({
  hasPurchasedCreditChecks,
  hasPurchasedSponsoredListing,
  hasPurchasedSyndications,
  purchasedCredits
}: Props) => {
  const { t } = useTranslation()
  const { visible, showModal, closeModal } = useDialogControllers({
    sponsoredListingDialog: false,
    tenantScreeningDialog: false,
    tenantScreeningOptionsDialog: false,
    syndicationDialog: false,
    warningDialog: false,
    addendumsDialog: false,
    buySyndicationCredits: false,
    buyTenantScreening: false,
    buySponsoredListing: false,
    advertiseYourListing: false,
    analyticsReport: false,
    useSyndicationCredits: false,
    checkoutDialog: false,
    editPaymentDialog: false,
    billingUser: false
  })
  const [selectedListing, setSelectedListing] = useState<{
    listingId: string
    address: string
  } | null>(null)
  const { listings: allMyListings } = useMyListings()
  const activeListings = filterActiveListings(allMyListings || [])

  const handleDialogOpen = useCallback(
    (id: modalOptions) => (isEmpty(activeListings) ? showModal('warningDialog') : showModal(id)),
    [activeListings, showModal]
  )
  const handleSyndicationSubmit = (listingData: {
    listing_id: string
    unit_number: string
    address: string
  }) => {
    setSelectedListing({ listingId: listingData.listing_id, address: getListingLabel(listingData) })
    showModal('useSyndicationCredits')()
  }

  const planText = useGetPlanKey()

  const columnDefinition: TableColumnDefinition = useMemo(
    () => [
      {
        accessor: 'title',
        Header: t('l.credit_usage.product.product.title', 'Product'),
        gridWidth: 'fill-available',
        disableSortBy: true,
        Cell: (cell: { value: string; row: { original: any } }) => {
          const Icon = cell?.row?.original?.icon
          const showInfoIcon = cell?.row?.original?.showInfoIcon
          return (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="flexStart"
              className={productStyle}
            >
              <Box paddingRight="xsmall">
                <Icon />
              </Box>
              <Text size="small">{cell?.value}</Text>
              {showInfoIcon && (
                <Box
                  onClick={cell?.row?.original?.infoAction}
                  paddingLeft="xxsmall"
                  cursor="pointer"
                >
                  <InfoCircleDarkBlue />
                </Box>
              )}
            </Box>
          )
        }
      },
      {
        accessor: 'credits',
        Header: t('l.credit_usage.product.credits.title', 'Credits'),
        gridWidth: 'fill-available',
        disableSortBy: true,
        Cell: ({ value }: { value?: string }) => {
          return value ? <Text size="small">{value}</Text> : null
        }
      },
      {
        accessor: 'buyMore',
        gridWidth: 'fill-available',
        disableSortBy: true,
        Cell: (cell: { value: string; row: { original: any } }) => {
          const value = cell?.row?.original
          return value?.buyMoreOnClick ? (
            <Box display="flex" flexDirection="column" alignItems={'flexEnd'} width="full">
              <Box style={{ width: '130px' }}>
                <Button
                  size="small"
                  variant="solid"
                  onClick={value?.buyMoreOnClick}
                  id={value?.buyMoreId}
                >
                  <Box width="full">{t('l.credit_usage.product.buy_more.action', 'Buy more')}</Box>
                </Button>
              </Box>
            </Box>
          ) : null
        }
      },
      {
        accessor: 'button',
        gridWidth: 'fill-available',
        disableSortBy: true,
        Cell: (cell: { value: string; row: { original: any } }) => {
          const value = cell?.row?.original
          return (
            <Box display="flex" flexDirection="column" alignItems={'flexEnd'} width="full">
              <Box style={{ width: '127px' }}>
                <Button
                  size="small"
                  variant="ghost"
                  onClick={value?.useCreditOnClick}
                  id={value?.useCreditId}
                  disabled={value?.useCreditDisabled}
                >
                  <Box width="full">
                    {t('l.credit_usage.product.use_credit.action', 'Use credit')}
                  </Box>
                </Button>
              </Box>
            </Box>
          )
        }
      }
    ],
    [t]
  )

  const TENANT_SCREENING: TableItem[] = useMemo(
    () => [
      {
        title: t('l.credit_usage.product.tenant_screening.title', 'Tenant Screening Report'),
        icon: TenantScreening,
        useCreditOnClick: handleDialogOpen('tenantScreeningOptionsDialog'),
        useCreditDisabled: purchasedCredits.creditChecks === 0,
        useCreditVariant: 'ghost',
        useCreditText: t('l.credit_usage.product.use_credit.action', 'Use credit'),
        credits: `${purchasedCredits.creditChecks}`,
        useCreditId: `usage-credit-${planText}-alacarte-tenant-screen-rpt-use-credits-btn-click`,
        buyMoreOnClick: showModal('buyTenantScreening'),
        buyMoreId: `usage-credit-${planText}-alacarte-sl-buy-more-btn-click`
      }
    ],
    [t, handleDialogOpen, planText, purchasedCredits.creditChecks, showModal]
  )

  const SPONSORED_LISTINGS: TableItem[] = useMemo(
    () => [
      {
        title: t('common.sponsored_listings', 'Sponsored Listings'),
        icon: RentalAdvertising,
        useCreditOnClick: handleDialogOpen('sponsoredListingDialog'),
        useCreditDisabled: purchasedCredits.sponsoredListings === 0,
        useCreditVariant: 'ghost',
        useCreditText: t('l.credit_usage.product.use_credit.action', 'Use credit'),
        credits: `${purchasedCredits.sponsoredListings} days`,
        useCreditId: `usage-credit-${planText}-alacarte-sl-use-credits-btn-click`,
        buyMoreOnClick: showModal('buySponsoredListing'),
        buyMoreId: `usage-credit-${planText}-alacarte-sl-buy-more-btn-click`
      }
    ],
    [t, handleDialogOpen, planText, purchasedCredits.sponsoredListings, showModal]
  )

  const SYNDICATION_CREDITS: TableItem[] = useMemo(
    () => [
      {
        title: t('common.syndications', 'Multi-Platform Advertising'),
        icon: RentalAdvertising,
        useCreditOnClick: handleDialogOpen('syndicationDialog'),
        useCreditDisabled: purchasedCredits.syndications === 0,
        useCreditVariant: 'ghost',
        useCreditText: t('l.credit_usage.product.use_credit.action', 'Use credit'),
        credits: `${purchasedCredits.syndications}`,
        buyMoreOnClick: showModal('buySyndicationCredits'),
        buyMoreId: `usage-credit-${planText}-alacarte-sl-buy-more-btn-click`,
        useCreditId: `usage-credit-${planText}-alacarte-syndication-use-credit-btn-click`,
        showInfoIcon: true,
        infoAction: showModal('advertiseYourListing')
      }
    ],
    [t, planText, purchasedCredits.syndications, showModal, handleDialogOpen]
  )

  const creditChecks = useMemo(
    () => (!!hasPurchasedCreditChecks ? TENANT_SCREENING : []),
    [hasPurchasedCreditChecks, TENANT_SCREENING]
  )
  const sponsoredListings = useMemo(
    () => (!!hasPurchasedSponsoredListing ? SPONSORED_LISTINGS : []),
    [hasPurchasedSponsoredListing, SPONSORED_LISTINGS]
  )
  const syndications = useMemo(
    () => (!!hasPurchasedSyndications ? SYNDICATION_CREDITS : []),
    [hasPurchasedSyndications, SYNDICATION_CREDITS]
  )
  const tableData = useMemo(
    () => [...creditChecks, ...syndications, ...sponsoredListings],
    [creditChecks, sponsoredListings, syndications]
  )

  return (
    <>
      <ResponsiveRender above="tablet">
        <Stack align="left" space="xsmall">
          <Box width="full" maxWidth="large">
            <Text weight="medium" size="medium" tone="secondary">
              {t('l.usage.credits.purchased.title', 'Purchased')}
            </Text>
          </Box>
          <Box width="full" maxWidth="large">
            <Table columns={columnDefinition} data={tableData} />
          </Box>
        </Stack>
      </ResponsiveRender>
      <ResponsiveRender below="desktop">
        <Stack align="left" space="xsmall">
          <Box display="flex">
            <Text weight="medium" size="medium" tone="secondary">
              {t('l.usage.credits.purchased.title', 'Purchased')}
            </Text>
          </Box>
          <Stack align="center" space="xsmall">
            {tableData?.map(item => {
              return <MobileCard card={item} key={item.title} />
            })}
          </Stack>
        </Stack>
      </ResponsiveRender>
      <DialogWrapper
        visible={visible}
        closeModal={closeModal}
        showModal={showModal}
        selectedListing={selectedListing}
        handleSyndicationSubmit={handleSyndicationSubmit}
      />
    </>
  )
}

export default ALaCarte
